import {useEffect, useRef} from 'react';
import {useMap} from 'react-leaflet';
import L from 'leaflet';
import {centreLibelle, libelleCourt, trouverCircuit} from '../../selectors/circuits.js';

/**
 * Calque circuits (surcouche `circuits=1`) : une enveloppe par circuit sélectionné, fond couleur claire à 35 %, contour 2 px,
 * libellé permanent. `enveloppes` : FeatureCollection (enveloppesCircuits) restreinte aux circuits sélectionnés.
 * Pane sous le canvas des PAV (zIndex 360, au-dessus des communes à 350) et non interactif : le clic est traité dans le `click`
 * de la carte (hit-test `trouverCircuit`), par le canvas des PAV quand il est monté (`delegue`), par cette couche sinon (style aplat).
 */
export default function CarteCircuitsLayer({enveloppes, onCircuit, delegue}) {
    const map = useMap();
    const rappel = useRef(onCircuit);
    rappel.current = onCircuit;
    const donnees = useRef(enveloppes);
    donnees.current = enveloppes;
    const couches = useRef(null);

    // Couches créées une fois ; le contenu est remplacé au changement de sélection.
    useEffect(() => {
        if (!map.getPane('carteCircuits')) {
            const pane = map.createPane('carteCircuits');
            pane.style.zIndex = 360;
            pane.style.pointerEvents = 'none';
        }
        const libelles = L.layerGroup();
        const polygones = L.geoJSON(null, {
            pane: 'carteCircuits',
            interactive: false,
            style: (f) => ({color: f.properties.couleur, weight: 2, opacity: 1, fillColor: f.properties.couleurClair, fillOpacity: 0.35}),
        });
        libelles.addTo(map);
        polygones.addTo(map);
        couches.current = {libelles, polygones};
        return () => {
            map.removeLayer(libelles);
            map.removeLayer(polygones);
            couches.current = null;
        };
    }, [map]);

    useEffect(() => {
        const c = couches.current;
        if (!c) return;
        c.polygones.clearLayers();
        c.libelles.clearLayers();
        if (!enveloppes) return;
        c.polygones.addData(enveloppes);
        for (const f of enveloppes.features) {
            L.tooltip({permanent: true, direction: 'center', className: 'carte-circuit-libelle', interactive: false})
                .setLatLng(centreLibelle(f))
                .setContent(libelleCourt(f.properties.libelle))
                .addTo(c.libelles);
        }
    }, [enveloppes]);

    useEffect(() => {
        if (delegue) return undefined;
        const surClic = (e) => {
            const f = donnees.current ? trouverCircuit(donnees.current, e.latlng.lat, e.latlng.lng) : null;
            if (f) rappel.current(f.properties.patrimoineId, !!(e.originalEvent && e.originalEvent.shiftKey));
        };
        map.on('click', surClic);
        return () => map.off('click', surClic);
    }, [map, delegue]);

    return null;
}
